import { useState, useEffect, useRef } from 'react';

/**
 * useMockLiveData — provides mock real-time protocol data with the SAME
 * shape that a real Sepolia hook (useSepoliaLive) will return in a future
 * iteration. Swap-in is trivial: replace this hook's import in components
 * with the real one.
 *
 * Returned shape:
 *   {
 *     blockNumber:    number,
 *     periodId:       number,
 *     ethThisPeriod:  number   (ETH, decimal),
 *     ethDelta1h:     number   (ETH, decimal — change in last hour),
 *     methMinted:     number,
 *     holders:        number,
 *     auditorsActive: number,
 *     secondsToNextPeriod: number,
 *     isLive:         boolean  (true once first read succeeded),
 *   }
 */
export default function useMockLiveData() {
  const [data, setData] = useState({
    blockNumber: 5234567,
    periodId: 12,
    ethThisPeriod: 0.0432,
    ethDelta1h: 0.0021,
    methMinted: 3420.18,
    methDeltaPeriod: 12.4,
    holders: 142,
    holdersDelta7d: 3,
    auditorsActive: 7,
    auditorsTotal: 7,
    proposalsOpen: 4,
    proposalsDisputed: 2,
    tvl: 0.318,
    tvlDelta30d: 0.041,
    secondsToNextPeriod: (2 * 86400) + (14 * 3600) + (33 * 60),
    isLive: true,
  });

  const dataRef = useRef(data);
  dataRef.current = data;

  // ETH ticker — mocks contributions arriving
  useEffect(() => {
    const id = setInterval(() => {
      if (Math.random() < 0.45) {
        setData(d => ({
          ...d,
          ethThisPeriod: +(d.ethThisPeriod + Math.random() * 0.0005).toFixed(4),
        }));
      }
    }, 2200);
    return () => clearInterval(id);
  }, []);

  // Block number tick (~12s like ethereum mainnet, here faster for visibility)
  useEffect(() => {
    const id = setInterval(() => {
      setData(d => ({ ...d, blockNumber: d.blockNumber + 1 }));
    }, 6000);
    return () => clearInterval(id);
  }, []);

  // Countdown
  useEffect(() => {
    const id = setInterval(() => {
      setData(d => ({
        ...d,
        secondsToNextPeriod: Math.max(0, d.secondsToNextPeriod - 1),
      }));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return data;
}

/** Format helpers reusable by the components. */
export function formatCountdown(seconds) {
  const safe = Math.max(0, Math.floor(seconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatBlock(n) {
  return '#' + n.toLocaleString('en-US');
}
